import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Phone, Calendar as CalendarIcon, Trash2, UserCheck, CheckCircle2,
} from "lucide-react";
import { endpoints } from "../lib/api";
import { waLink, openWhatsApp, templates } from "../lib/wa";
import WaButton from "../components/WaButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import ChannelPicker from "../components/ChannelPicker";
import AISuggestPanel from "../components/AISuggestPanel";
import { LeadHeatBadge, ConversionLikelihoodBadge } from "../components/ScoringBadges";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";

const STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "trial_booked", label: "Trial Booked" },
  { key: "trial_done", label: "Trial Done" },
  { key: "converted", label: "Converted" },
  { key: "lost", label: "Lost" },
];

const MSG_TEMPLATES = [
  { key: "welcome", label: "Welcome" },
  { key: "follow_up", label: "Follow-up" },
  { key: "trial_reminder", label: "Trial reminder" },
  { key: "trial_feedback", label: "Trial feedback" },
  { key: "custom", label: "Custom" },
];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [msgKind, setMsgKind] = useState("follow_up");
  const [msgText, setMsgText] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [plan, setPlan] = useState({ plan: "Monthly", duration_days: 30, plan_amount: 2000 });
  const [channels, setChannels] = useState(["whatsapp"]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.getLead(id);
      setLead(data);
      setNotes(data.notes || "");
    } catch {
      toast.error("Lead not found");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Auto-open WhatsApp when arriving from Task "Manage" CTA (?wa=task)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!lead || searchParams.get("wa") !== "task") return;
    openWhatsApp(lead.phone, templates.follow_up(lead.name));
    const next = new URLSearchParams(searchParams);
    next.delete("wa");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);

  useEffect(() => {
    if (!lead) return;
    // prefill template
    const tmpl = templates[msgKind];
    if (!tmpl) return;
    if (msgKind === "welcome") setMsgText(tmpl(lead.name));
    else if (msgKind === "follow_up") setMsgText(tmpl(lead.name));
    else if (msgKind === "trial_reminder") setMsgText(tmpl(lead.name, formatDate(lead.trial_date) || "your scheduled date"));
    else if (msgKind === "trial_feedback") setMsgText(tmpl(lead.name));
    else setMsgText("");
  }, [msgKind, lead]);

  if (loading || !lead) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const setStatus = async (status) => {
    await endpoints.updateLead(id, { status });
    toast.success(`Status: ${STAGES.find(s => s.key === status)?.label}`);
    load();
  };

  const saveNotes = async () => {
    await endpoints.updateLead(id, { notes });
    toast.success("Notes saved");
  };

  const setTrialDate = async (date) => {
    await endpoints.updateLead(id, { trial_date: date.toISOString() });
    toast.success("Trial date set");
    load();
  };

  const markTrial = async (trial_status) => {
    const updates = { trial_status };
    if (trial_status === "attended") updates.status = "trial_done";
    if (trial_status === "no_show") updates.status = "lost";
    await endpoints.updateLead(id, updates);
    toast.success(`Marked ${trial_status.replace("_", " ")}`);
    load();
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    await endpoints.logLeadMessage(id, { kind: msgKind, text: msgText, channels });
    if (channels.includes("whatsapp")) {
      openWhatsApp(lead.phone, msgText);
    }
    const suffix = channels.length === 1 ? channels[0] : `${channels.length} channels`;
    toast.success(`Logged via ${suffix}${channels.includes("whatsapp") ? " · opened in WhatsApp" : ""}`);
    setMsgText("");
    setMsgKind("follow_up");
    load();
  };

  const convertToMember = async (e) => {
    e.preventDefault();
    await endpoints.convertLead(id, {
      name: lead.name,
      phone: lead.phone,
      plan: plan.plan,
      plan_amount: parseFloat(plan.plan_amount) || 0,
      duration_days: parseInt(plan.duration_days) || 30,
    });
    toast.success("Converted to member 🎉");
    setConvertOpen(false);
    navigate("/members");
  };

  const del = async () => {
    if (!window.confirm("Delete this lead?")) return;
    await endpoints.deleteLead(id);
    toast.success("Lead deleted");
    navigate("/leads");
  };

  const timeline = [
    { type: "created", label: "Lead created", at: lead.created_at, icon: UserCheck },
    ...lead.messages.map((m) => ({
      type: "msg", label: `${labelize(m.kind)} message sent`, at: m.created_at, icon: MessageCircle, text: m.text,
    })),
    lead.trial_date && {
      type: "trial", label: `Trial scheduled for ${formatDate(lead.trial_date)}`, at: lead.trial_date, icon: CalendarIcon,
    },
    lead.trial_status === "attended" && {
      type: "trial_done", label: "Trial attended", at: lead.updated_at, icon: CheckCircle2,
    },
  ].filter(Boolean).sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl mx-auto">
      <Link to="/leads" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-leads">
        <ArrowLeft className="w-4 h-4" /> Leads
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="lead-detail-name">
              {lead.name}
            </h1>
            <LeadHeatBadge lead={lead} />
            {(lead.trial_date || lead.trial_status) && <ConversionLikelihoodBadge lead={lead} />}
          </div>
          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{lead.phone}</span>
            <span className="text-muted-foreground/50">·</span>
            <span>{lead.source}</span>
            {lead.interest && <><span className="text-muted-foreground/50">·</span><span>{lead.interest}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <WaButton
            phone={lead.phone}
            text={templates.follow_up(lead.name)}
            testId="lead-whatsapp-primary"
            variant="primary"
            size="lg"
          >
            <MessageCircle className="w-4 h-4" />
            Message on WhatsApp
          </WaButton>
          <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
            <DialogTrigger asChild>
              <button
                disabled={lead.status === "converted"}
                data-testid="convert-btn"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" /> Convert
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Convert to member</DialogTitle></DialogHeader>
              <form onSubmit={convertToMember} className="space-y-4">
                <div>
                  <Label>Plan</Label>
                  <Input value={plan.plan} onChange={e => setPlan({ ...plan, plan: e.target.value })} data-testid="convert-plan-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Duration (days)</Label>
                    <Input type="number" value={plan.duration_days} onChange={e => setPlan({ ...plan, duration_days: e.target.value })} />
                  </div>
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" value={plan.plan_amount} onChange={e => setPlan({ ...plan, plan_amount: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <button type="submit" className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90 transition-colors">
                    Convert
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <button onClick={del} data-testid="delete-lead-btn" className="p-2.5 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: actions & notes */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>Status & Trial</CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Pipeline status</Label>
                <Select value={lead.status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trial date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="trial-date-btn"
                      className="w-full text-left border border-border rounded-lg px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center gap-2"
                    >
                      <CalendarIcon className="w-4 h-4 text-muted-foreground/70" />
                      {lead.trial_date ? formatDate(lead.trial_date) : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start">
                    <Calendar
                      mode="single"
                      selected={lead.trial_date ? new Date(lead.trial_date) : undefined}
                      onSelect={(d) => d && setTrialDate(d)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            {lead.status === "trial_booked" && (
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => markTrial("attended")}
                  data-testid="mark-attended-btn"
                  className="inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Mark attended
                </button>
                <button
                  onClick={() => markTrial("no_show")}
                  data-testid="mark-noshow-btn"
                  className="inline-flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
                >
                  No-show
                </button>
              </div>
            )}
          </Card>

          <AISuggestPanel
            kind={lead.trial_status === "attended" ? "trial_close" : "lead_followup"}
            targetKind="lead"
            targetId={lead.id}
            phone={lead.phone}
          />

          <Card>
            <CardHeader>Send WhatsApp</CardHeader>
            <div className="space-y-3">
              <div>
                <Label>Template</Label>
                <Select value={msgKind} onValueChange={setMsgKind}>
                  <SelectTrigger data-testid="template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MSG_TEMPLATES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  data-testid="message-textarea"
                  value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                  rows={4}
                />
              </div>
              <div>
                <Label>Send via</Label>
                <ChannelPicker value={channels} onChange={setChannels} testidPrefix="lead-channel" />
                <p className="text-[11px] text-muted-foreground/70 mt-1.5">SMS & Email are logged locally in this MVP - no real send.</p>
              </div>
              <button
                onClick={sendMessage}
                data-testid="send-wa-btn"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-medium px-4 py-2 rounded-lg transition-colors active:scale-95"
              >
                <MessageCircle className="w-4 h-4" /> Send & log
              </button>
            </div>
          </Card>

          <Card>
            <CardHeader>Notes</CardHeader>
            <Textarea
              data-testid="notes-textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Private notes about this lead…"
              rows={4}
            />
          </Card>
        </div>

        {/* Right: timeline */}
        <div>
          <Card>
            <CardHeader>Timeline</CardHeader>
            <ol className="space-y-4">
              {timeline.map((t, i) => {
                const Icon = t.icon;
                return (
                  <li key={i} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground">{t.label}</div>
                      {t.text && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.text}</div>}
                      <div className="text-[11px] text-muted-foreground/70 mt-0.5">{formatDateTime(t.at)}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-card border border-border rounded-xl p-5">{children}</div>;
}
function CardHeader({ children }) {
  return <h3 className="font-display text-base font-semibold text-foreground mb-4">{children}</h3>;
}
function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function formatDateTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function labelize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}
